import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import BackButton from "@/components/BackButton";

export default function Register() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [privacy, setPrivacy] = useState(false);
    const [marketing, setMarketing] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        if (!privacy) {
            setError("Devi accettare l'informativa privacy per registrarti.");
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await api.post("/auth/register", {
                email,
                password,
                privacy_accepted: privacy,
                marketing_consent: marketing,
            });
            toast.success("Registrazione riuscita. Attendi la verifica email.");
            navigate("/login");
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || "Registrazione non riuscita.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="co-container py-14 sm:py-20 max-w-md" data-testid="register-page">
            <div className="mb-4"><BackButton testid="register-back-btn" /></div>
            <span className="co-caption">Nuovo Account</span>
            <h1 className="font-serif text-4xl mt-2 mb-3">Registrati</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-8">
                Dopo la registrazione riceverai un link di verifica per confermare l'account e sbloccare i prezzi.
            </p>

            <form onSubmit={submit} className="space-y-5">
                <div>
                    <Label htmlFor="email" className="co-caption mb-2 block">Email</Label>
                    <Input
                        id="email" type="email" required autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        data-testid="register-email-input"
                        className="bg-white h-11"
                    />
                </div>
                <div>
                    <Label htmlFor="password" className="co-caption mb-2 block">Password (min. 8 caratteri)</Label>
                    <Input
                        id="password" type="password" required minLength={8} autoComplete="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        data-testid="register-password-input"
                        className="bg-white h-11"
                    />
                </div>

                <div className="space-y-3 pt-2">
                    <label className="flex items-start gap-3 cursor-pointer text-sm">
                        <Checkbox
                            checked={privacy}
                            onCheckedChange={setPrivacy}
                            data-testid="register-privacy-checkbox"
                            className="mt-0.5"
                        />
                        <span className="text-[hsl(var(--muted-foreground))]">
                            Accetto l'<Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--primary))] underline underline-offset-2" data-testid="register-privacy-link">informativa sulla privacy</Link> e il trattamento dei dati personali. <span className="text-[hsl(var(--destructive))]">(obbligatorio)</span>
                        </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer text-sm">
                        <Checkbox
                            checked={marketing}
                            onCheckedChange={setMarketing}
                            data-testid="register-marketing-checkbox"
                            className="mt-0.5"
                        />
                        <span className="text-[hsl(var(--muted-foreground))]">
                            Desidero ricevere comunicazioni promozionali e novità della cantina. (facoltativo)
                        </span>
                    </label>
                </div>

                {error && (
                    <p className="text-sm text-[hsl(var(--destructive))]" data-testid="register-error">
                        {error}
                    </p>
                )}

                <button type="submit" disabled={busy} className="co-btn-primary w-full h-12" data-testid="register-submit-btn">
                    {busy ? "Registrazione in corso…" : "Crea account"}
                </button>
            </form>

            <p className="mt-8 text-sm text-[hsl(var(--muted-foreground))]">
                Hai già un account?{" "}
                <Link to="/login" className="text-[hsl(var(--primary))] hover:underline underline-offset-4">
                    Accedi
                </Link>
            </p>
        </div>
    );
}
